/**
 * CcSubscribePage — self-serve credit-card subscribe.
 * Shows current subscription + plan picker + confirm step + cancel.
 */
import { useState, type FormEvent } from "react";
import {
	useCcSubscribeConfirm,
	useCcSubscribeCreate,
	useCcSubscribeDelete,
	useCcSubscribeList,
} from "./hooks";
import type {
	CcSubscribePlanId,
	CreateIntentResponse,
} from "./api";

const CARD = "rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const INPUT =
	"w-full rounded-[6px] border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-900 font-[Inter] focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-500/20";
const SELECT =
	"w-full rounded-[6px] border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-900 font-[Inter] focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-500/20";
const BTN_PRIMARY =
	"rounded-[6px] border border-green-600 bg-green-600 px-3 py-1.5 text-sm font-medium text-white font-[Inter] hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"rounded-[6px] border border-zinc-200 bg-white px-2.5 py-1 text-xs font-medium text-zinc-900 font-[Inter] hover:bg-green-50";
const BTN_DANGER =
	"rounded-[6px] border border-zinc-200 bg-white px-2.5 py-1 text-xs font-medium text-zinc-900 font-[Inter] hover:border-green-600 hover:bg-green-50";

const PLANS: Array<{ code: CcSubscribePlanId; label: string }> = [
	{ code: "starter", label: "Starter" },
	{ code: "growth", label: "Growth" },
	{ code: "pro", label: "Pro" },
	{ code: "enterprise", label: "Enterprise" },
];

function StatusPill({ status }: { status: string }) {
	const base =
 "inline-flex items-center gap-1.5 rounded-[6px] px-2 py-0.5 text-xs font-medium font-[Inter]";
	const dot = "inline-block h-1.5 w-1.5 rounded-full";
	if (status === "active") {
 return (
 <span className={`${base} border border-green-600 bg-green-50 text-green-700`}>
 <span className={`${dot} bg-green-600`} />
 active
 </span>
 );
	}
	return (
 <span className={`${base} border border-zinc-200 bg-white text-zinc-500`}>
 <span className={`${dot} bg-zinc-500`} />
 {status}
 </span>
	);
}

export function CcSubscribePage() {
	const meQuery = useCcSubscribeList();
	const intentMut = useCcSubscribeCreate();
	const confirmMut = useCcSubscribeConfirm();
	const cancelMut = useCcSubscribeDelete();

	const [tenantId, setTenantId] = useState("");
	const [planCode, setPlanCode] = useState<CcSubscribePlanId>("starter");
	const [currency, setCurrency] = useState("USD");
	const [cardToken, setCardToken] = useState("");
	const [intent, setIntent] = useState<CreateIntentResponse | null>(null);

	const sub = meQuery.data?.subscription ?? null;

	const handleCreateIntent = (e: FormEvent) => {
 e.preventDefault();
 if (!tenantId.trim()) return;
 intentMut.mutate(
 {
 tenant_id: tenantId.trim(),
 plan_code: planCode,
 currency: currency.trim() || "USD",
 },
 {
 onSuccess: (data) => {
 setIntent(data);
 },
 },
 );
	};

	const handleConfirm = (e: FormEvent) => {
 e.preventDefault();
 if (!intent || !cardToken.trim()) return;
 confirmMut.mutate(
 {
 intent_id: intent.payment_intent_id,
 card_token_mock: cardToken.trim(),
 },
 {
 onSuccess: () => {
 setIntent(null);
 setCardToken("");
 },
 },
 );
	};

	return (
 <main
 data-testid="cc-subscribe-page"
 className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10 font-[Inter]"
 >
 <header className="flex flex-col gap-2">
 <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
 cc-subscribe
 </p>
 <h1 className="text-3xl font-semibold text-green-700">
 Credit-card subscribe
 </h1>
 <p className="text-sm text-zinc-500">
 Create a payment intent, confirm with a mock card token, and
 manage your subscription.
 </p>
 </header>

 <section className={CARD} aria-label="Current subscription">
 <div className="flex items-center justify-between">
 <h2 className="text-green-700 font-semibold text-base font-semibold text-zinc-900">
 Current subscription
 </h2>
 {sub ? <StatusPill status={sub.status} /> : null}
 </div>
 {meQuery.isLoading ? (
 <p
 className="mt-3 text-sm text-zinc-500"
 data-testid="cc-subscribe-loading"
 >
 Loading.
 </p>
 ) : meQuery.error ? (
 <p className="mt-3 text-sm text-green-700" role="alert">
 {meQuery.error.message}
 </p>
 ) : !sub ? (
 <p
 className="mt-3 text-sm text-zinc-500"
 data-testid="cc-subscribe-empty"
 >
 No subscription yet. Create a payment intent below to get
 started.
 </p>
 ) : (
 <dl
 className="mt-4 grid grid-cols-2 gap-4 text-sm"
 data-testid="cc-subscribe-list"
 >
 <div>
 <dt className="text-xs uppercase tracking-wider text-zinc-500">
 Plan
 </dt>
 <dd className="mt-1 font-medium text-zinc-900">
 {sub.plan}
 </dd>
 </div>
 <div>
 <dt className="text-xs uppercase tracking-wider text-zinc-500">
 Price
 </dt>
 <dd className="mt-1 font-mono text-zinc-900">
 {sub.formatted_amount ??
 `${sub.price_minor} ${sub.currency}`}
 </dd>
 </div>
 <div>
 <dt className="text-xs uppercase tracking-wider text-zinc-500">
 Cycle
 </dt>
 <dd className="mt-1 text-zinc-900">
 {sub.cycle_days} days
 </dd>
 </div>
 <div>
 <dt className="text-xs uppercase tracking-wider text-zinc-500">
 Next bill
 </dt>
 <dd className="mt-1 font-mono text-xs text-zinc-900">
 {sub.next_bill_at
 ? new Date(sub.next_bill_at).toISOString()
 : "-"}
 </dd>
 </div>
 <div className="col-span-2">
 <dt className="text-xs uppercase tracking-wider text-zinc-500">
 Subscription id
 </dt>
 <dd
 className="mt-1 font-mono text-xs text-zinc-500"
 data-testid={`cc-subscribe-row-${sub.id}`}
 >
 {sub.id}
 </dd>
 </div>
 {sub.status === "active" ? (
 <div className="col-span-2">
 <button
 type="button"
 className={BTN_DANGER}
 onClick={() => cancelMut.mutate(sub.id)}
 disabled={cancelMut.isPending}
 data-testid={`cc-subscribe-delete-${sub.id}`}
 >
 {cancelMut.isPending
 ? "Cancelling."
 : "Cancel subscription"}
 </button>
 </div>
 ) : null}
 </dl>
 )}
 {cancelMut.error ? (
 <p className="mt-3 text-xs text-green-700" role="alert">
 {cancelMut.error.message}
 </p>
 ) : null}
 </section>

 <section className={CARD} aria-label="Create payment intent">
 <h2 className="text-green-700 font-semibold text-base font-semibold text-zinc-900">
 Create payment intent
 </h2>
 <form
 onSubmit={handleCreateIntent}
 className="mt-3 grid gap-3 sm:grid-cols-3"
 data-testid="cc-subscribe-create-form"
 >
 <label className="flex flex-col gap-1 text-xs text-zinc-500">
 Tenant id
 <input
 type="text"
 className={INPUT}
 placeholder="t_1234"
 value={tenantId}
 onChange={(e) => setTenantId(e.currentTarget.value)}
 disabled={intentMut.isPending}
 data-testid="cc-subscribe-name-input"
 />
 </label>
 <label className="flex flex-col gap-1 text-xs text-zinc-500">
 Plan
 <select
 className={SELECT}
 value={planCode}
 onChange={(e) =>
 setPlanCode(
 e.currentTarget.value as CcSubscribePlanId,
 )
 }
 disabled={intentMut.isPending}
 >
 {PLANS.map((p) => (
 <option key={p.code} value={p.code}>
 {p.label}
 </option>
 ))}
 </select>
 </label>
 <label className="flex flex-col gap-1 text-xs text-zinc-500">
 Currency
 <input
 type="text"
 className={INPUT}
 placeholder="USD"
 value={currency}
 onChange={(e) => setCurrency(e.currentTarget.value)}
 disabled={intentMut.isPending}
 maxLength={3}
 />
 </label>
 <div className="sm:col-span-3">
 <button
 type="submit"
 className={BTN_PRIMARY}
 disabled={intentMut.isPending || !tenantId.trim()}
 data-testid="cc-subscribe-submit"
 >
 {intentMut.isPending ? "Saving." : "Create intent"}
 </button>
 </div>
 </form>
 {intentMut.error ? (
 <p className="mt-2 text-xs text-green-700" role="alert">
 {intentMut.error.message}
 </p>
 ) : null}
 </section>

 {intent ? (
 <section className={CARD} aria-label="Confirm payment intent">
 <h2 className="text-green-700 font-semibold text-base font-semibold text-zinc-900">
 Confirm payment
 </h2>
 <dl className="mt-3 grid grid-cols-2 gap-3 text-sm">
 <div>
 <dt className="text-xs uppercase tracking-wider text-zinc-500">
 Intent id
 </dt>
 <dd
 className="mt-1 font-mono text-xs text-zinc-900"
 data-testid="cc-subscribe-intent-id"
 >
 {intent.payment_intent_id}
 </dd>
 </div>
 <div>
 <dt className="text-xs uppercase tracking-wider text-zinc-500">
 Amount
 </dt>
 <dd className="mt-1 font-mono text-zinc-900">
 {intent.formatted_amount}
 </dd>
 </div>
 </dl>
 <form
 onSubmit={handleConfirm}
 className="mt-4 flex flex-col gap-3 sm:flex-row"
 data-testid="cc-subscribe-confirm-form"
 >
 <input
 type="text"
 className={INPUT}
 placeholder="tok_mock_visa"
 value={cardToken}
 onChange={(e) => setCardToken(e.currentTarget.value)}
 disabled={confirmMut.isPending}
 data-testid="cc-subscribe-card-input"
 />
 <button
 type="submit"
 className={BTN_PRIMARY}
 disabled={confirmMut.isPending || !cardToken.trim()}
 data-testid="cc-subscribe-confirm-submit"
 >
 {confirmMut.isPending ? "Confirming." : "Confirm"}
 </button>
 <button
 type="button"
 className={BTN_GHOST}
 onClick={() => setIntent(null)}
 disabled={confirmMut.isPending}
 >
 Discard
 </button>
 </form>
 {confirmMut.error ? (
 <p className="mt-2 text-xs text-green-700" role="alert">
 {confirmMut.error.message}
 </p>
 ) : null}
 </section>
 ) : null}
 </main>
	);
}

export default CcSubscribePage;
